import { useState } from "react";
import {
  Flame, AlertTriangle, Ghost, Target, Clock, ArrowRight, Zap,
  CheckSquare, Check, MessageCircle, Calendar, Users, ShieldCheck,
} from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { whatsappUrl } from "@/lib/whatsapp";

const TIDYCAL_URL = "https://tidycal.com/shinrai/bootcampb2b";
const ELECTRIC = "#00BFFF";
const BG = "#0a0e14";
const PANEL = "#131a26";
const PANEL_BORDER = "#1f2a3d";

/* ------- Reusable bits ------- */
function SectionTitle({ overline, children, testid }) {
  return (
    <div className="mb-10">
      <h2
        className="font-mono font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-pearl mb-3"
        data-testid={testid}
      >
        {children}
      </h2>
      <div
        className="w-16 h-[3px]"
        style={{
          background: `linear-gradient(90deg, ${ELECTRIC}, transparent)`,
        }}
      />
      {overline && (
        <div className="font-mono text-xs tracking-[0.22em] uppercase text-pearl/45 mt-3">
          {overline}
        </div>
      )}
    </div>
  );
}

function BluePanel({ children, className = "", testid }) {
  return (
    <div
      className={`relative border bg-[${PANEL}]/70 backdrop-blur-sm ${className}`}
      style={{ borderColor: PANEL_BORDER, backgroundColor: `${PANEL}cc` }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function PrimaryCTA({ href, children, testid, large = true }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`group inline-flex items-center gap-4 font-mono font-bold tracking-[0.18em] uppercase transition-all ${
        large ? "px-8 py-5 text-sm" : "px-6 py-3.5 text-xs"
      }`}
      style={{
        backgroundColor: ELECTRIC,
        color: BG,
        boxShadow: `0 0 0 1px ${ELECTRIC}, 0 0 32px rgba(0,191,255,0.35)`,
      }}
    >
      <span>{children}</span>
      <span
        className="flex items-center justify-center w-6 h-6 transition-transform group-hover:translate-x-1"
        style={{ backgroundColor: BG, color: ELECTRIC }}
      >
        <ArrowRight size={14} strokeWidth={2.5} />
      </span>
    </a>
  );
}

/* ------- Data ------- */
const QUICK_CARDS = [
  {
    icon: Flame,
    title: "Le Patron Pompier",
    text: "Tu éteins des incendies toute la journée.",
    img: "https://images.unsplash.com/photo-1758520144705-b39e11ff32e3?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: AlertTriangle,
    title: "Le Bureau Décharge",
    text: "Tu es le goulot d'étranglement de ta boîte.",
    img: "https://images.unsplash.com/photo-1758874383583-59c39da93e40?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: Ghost,
    title: "Le Fantôme à la Maison",
    text: "Tu es là physiquement, mais ton esprit est sur tes problèmes de prod.",
    img: "https://images.unsplash.com/photo-1674156423391-a65ab2a435de?auto=format&fit=crop&w=900&q=80",
  },
];

const DETAILED_CASES = [
  {
    icon: Flame,
    title: "Le Patron Pompier",
    body: "Il est 8h30. Le téléphone sonne déjà. Une urgence. Puis une autre. Puis un client mécontent. Puis un fournisseur qui ne livre pas. Tu passes ta journée entière à éteindre des incendies — tu cours d'un couloir à l'autre, tu réponds à 47 messages WhatsApp, et le soir à 20h, tu n'as pas avancé d'un millimètre sur ta vraie priorité. Tu rentres épuisé, sans avoir rien construit. Demain ce sera pareil. C'est un flux décisionnel cassé qui remonte tout vers toi.",
    diagnosis: "Ce n'est pas un problème de discipline. C'est un SYSTÈME cassé.",
    livrable: "#2 — Cartographie des Irritants",
  },
  {
    icon: AlertTriangle,
    title: "Le Bureau Décharge",
    body: "Des dossiers partout. Des Post-its collés sur l'écran. Trois tableaux Excel ouverts dont deux sont périmés. Tu cherches le devis du client Dupont depuis 12 minutes. Tes collaborateurs font pareil — chacun a son propre « système » qui ne ressemble à celui de personne. L'information est partout et nulle part. Le temps s'évapore dans la recherche, pas dans la création de valeur. Un stock d'informations dormant que personne ne sait exploiter.",
    diagnosis: "Coût réel : 1h30/jour perdue = 30 jours ENTIERS par an brûlés dans le chaos.",
    livrable: "#1 — Audit Lean Express",
  },
  {
    icon: Ghost,
    title: "Le Fantôme à la Maison",
    body: "Vendredi soir. Ta fille te demande : « T'étais où cette semaine, papa ? ». Tu es rentré après leur coucher lundi, mardi, jeudi. Mercredi tu étais en déplacement. Ce n'est pas un sacrifice temporaire — c'est devenu la normale depuis 2 ans. Tu as créé ta boîte pour être libre. Tu es devenu le prisonnier le mieux payé de ton propre système. Tu ES le goulot d'étranglement de ta propre vie.",
    diagnosis: "Coût réel : Ta santé, tes relations, ta raison de te lever le matin.",
    livrable: "#9 — CEO Habits Canvas",
  },
  {
    icon: Target,
    title: "Les Chiffres Fantômes",
    body: "C'est le 15 du mois. Tu demandes à ta comptable les chiffres de mars. Elle n'a pas encore tout rentré. Tu navigues à vue depuis 6 semaines. Tu prends des décisions de recrutement, d'investissement, de pricing — sans savoir réellement où tu en es. Tu découvres parfois en octobre que tu as perdu de l'argent en mars. C'est conduire une voiture les yeux bandés en regardant dans le rétroviseur. Le flux d'information financière est cassé à la source.",
    diagnosis: "Coût réel : Des milliers d'euros d'erreurs évitables prises à l'aveugle.",
    livrable: "#10 — Dashboard KPIs Pilotage",
  },
  {
    icon: Clock,
    title: "Le Cerveau qui Ne S'Éteint Plus",
    body: "23h30. Tu es allongé. Ton corps est épuisé. Ton cerveau tourne encore : le devis de lundi, le conflit avec Martin, la trésorerie de juillet, l'appel commercial de demain… Tu ne dors pas vraiment. Tu te lèves fatigué. Tu prends des décisions sous fatigue chronique. Et les décisions sous fatigue sont les pires décisions. Ton cerveau est devenu le buffer de stockage de toute l'entreprise.",
    diagnosis: "Coût réel : Une capacité de jugement dégradée précisément quand l'entreprise a le plus besoin de toi.",
    livrable: "#4 — Guide Neurosciences Dirigeant",
  },
];

const CHECKLIST = [
  "Tu éteins des incendies dès le café ?",
  "Ton équipe attend tout de toi pour décider ?",
  "Tes chiffres arrivent avec 2 mois de retard ?",
  "Ton cerveau tourne à 200% même dans ton lit à 23h30 ?",
  "Tu as l'impression de vider l'océan à la petite cuillère ?",
  "Tes réunions de 2h ne servent à rien ?",
];

const DELIVERABLES = [
  "Audit Lean Express (Ton score de santé réelle)",
  "Cartographie des Irritants",
  "VSM Current State (La photo de ton chaos)",
  "Guide Neurosciences Dirigeant",
  "Matrice des Biais Décisionnels",
  "Quick Wins Matrix",
  "Guide IA Opérationnelle (Tes automatisations)",
  "VSM Future State (Ton business simplifié)",
  "CEO Habits Canvas",
  "Dashboard KPIs Pilotage",
  "Roadmap 90 Jours & Kaizen Board",
];

/* ------- The page ------- */
export default function BootcampPage() {
  useDocumentTitle(
    "Bootcamp B2B — Pilote en 30 jours",
    "Bootcamp B2B ShinRai · Reprends le contrôle de ton entreprise avec le système de pilotage tri-disciplinaire (Lean, Neurosciences, IA). 30 jours, 11 livrables actionnables, 10 places."
  );

  const [accept, setAccept] = useState(false);

  return (
    <main
      className="text-pearl"
      style={{ backgroundColor: BG }}
      data-testid="bootcamp-page"
    >
      {/* ============================== HERO ============================== */}
      <section
        className="relative overflow-hidden border-b"
        style={{ borderColor: PANEL_BORDER }}
      >
        {/* Blueprint grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,191,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,191,255,0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(0,191,255,0.18), transparent)",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-36 pb-24 sm:pt-44 sm:pb-32 text-center">
          <div className="font-mono text-xs tracking-[0.28em] uppercase mb-6" style={{ color: ELECTRIC }}>
            // ShinRai Consulting · Bootcamp B2B
          </div>

          <h1
            className="font-mono font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl"
            data-testid="bootcamp-hero-title"
          >
            <span className="block text-pearl">STOP L&apos;ASPHYXIE,</span>
            <span
              className="block mt-2"
              style={{ color: ELECTRIC, textShadow: "0 0 28px rgba(0,191,255,0.35)" }}
            >
              REPRENDS LES COMMANDES.
            </span>
          </h1>

          <p className="mt-10 text-lg sm:text-xl text-pearl/75 leading-relaxed max-w-3xl mx-auto">
            Cessez d&apos;être le goulot d&apos;étranglement de votre propre boîte.
            On installe votre cockpit de pilotage en{" "}
            <span className="text-pearl font-semibold">30 jours</span>.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#situation"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("situation");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="bootcamp-hero-cta"
              className="group inline-flex items-center gap-4 px-8 py-5 font-mono font-bold text-sm tracking-[0.18em] uppercase transition-all"
              style={{
                backgroundColor: ELECTRIC,
                color: BG,
                boxShadow: `0 0 0 1px ${ELECTRIC}, 0 0 32px rgba(0,191,255,0.35)`,
              }}
            >
              <span>Reprendre le contrôle maintenant</span>
              <span
                className="flex items-center justify-center w-6 h-6 transition-transform group-hover:translate-y-0.5"
                style={{ backgroundColor: BG, color: ELECTRIC }}
              >
                <ArrowRight size={14} strokeWidth={2.5} className="rotate-90" />
              </span>
            </a>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-pearl/45">
              · 10 places · Sélection par appel
            </span>
          </div>
        </div>
      </section>

      {/* ============================== QUICK CARDS — Situation ============================== */}
      <section id="situation" className="relative py-20 sm:py-28 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <SectionTitle testid="bootcamp-situation-title">
            Ta situation sans filtre
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {QUICK_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <BluePanel
                  key={c.title}
                  className="overflow-hidden"
                  testid={`situation-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="aspect-[16/10] bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${c.img})` }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(10,14,20,0.85), rgba(10,14,20,0.2))",
                      }}
                    />
                    <Icon
                      size={32}
                      className="absolute bottom-4 left-4"
                      style={{ color: ELECTRIC }}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-mono font-bold text-lg uppercase mb-2 text-pearl">
                      {c.title}
                    </h3>
                    <p className="text-pearl/65 text-sm leading-relaxed">{c.text}</p>
                  </div>
                </BluePanel>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================== DETAILED CASES ============================== */}
      <section
        className="relative py-20 sm:py-28 border-y"
        style={{ borderColor: PANEL_BORDER, backgroundColor: "#0c1219" }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <SectionTitle overline="Cas pratiques" testid="bootcamp-cases-title">
            Se reconnaître
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {DETAILED_CASES.map((c) => {
              const Icon = c.icon;
              return (
                <BluePanel
                  key={c.title}
                  className="p-7 sm:p-8"
                  testid={`case-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <Icon size={22} style={{ color: ELECTRIC }} />
                    <h3 className="font-mono font-bold text-xl uppercase text-pearl">
                      {c.title}
                    </h3>
                  </div>
                  <p className="text-pearl/75 leading-relaxed text-sm mb-6">
                    {c.body}
                  </p>
                  <div
                    className="border-t pt-5 space-y-2 text-sm"
                    style={{ borderColor: PANEL_BORDER }}
                  >
                    <p className="text-pearl">
                      <span className="text-pearl/55">→ </span>
                      {c.diagnosis}
                    </p>
                    <p>
                      <span className="text-pearl/55">→ Livrable ShinRai : </span>
                      <span style={{ color: ELECTRIC }} className="font-semibold">
                        {c.livrable}
                      </span>
                    </p>
                  </div>
                </BluePanel>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================== CHECKLIST ============================== */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <SectionTitle testid="bootcamp-checklist-title">
            Et toi, tu coches combien de cases ?
          </SectionTitle>

          <BluePanel className="p-6 sm:p-10">
            <ul className="divide-y" style={{ borderColor: PANEL_BORDER }}>
              {CHECKLIST.map((item, i) => (
                <li
                  key={item}
                  className="flex items-start gap-4 py-4"
                  style={i > 0 ? { borderTop: `1px solid ${PANEL_BORDER}` } : {}}
                  data-testid={`checklist-item-${i + 1}`}
                >
                  <CheckSquare
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: ELECTRIC }}
                  />
                  <span className="text-pearl/90 leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          </BluePanel>
        </div>
      </section>

      {/* ============================== WAKE-UP CALL ============================== */}
      <section
        className="relative py-24 sm:py-32 border-y"
        style={{ borderColor: PANEL_BORDER, backgroundColor: "#0c1219" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
          <AlertTriangle
            size={56}
            className="mx-auto mb-8"
            style={{
              color: ELECTRIC,
              filter: "drop-shadow(0 0 22px rgba(0,191,255,0.55))",
            }}
          />
          <h2
            className="font-mono font-bold uppercase text-3xl sm:text-4xl tracking-tight mb-8 text-pearl"
            data-testid="bootcamp-wakeup-title"
          >
            Ce que cela signifie réellement…
          </h2>
          <p className="text-lg text-pearl/80 leading-relaxed">
            Tu n&apos;as plus une entreprise. Tu as un emploi épuisant que tu te
            paies toi-même. Tu es le goulot d&apos;étranglement de ta propre
            croissance. Chaque douleur ci-dessus n&apos;est pas un accident —
            c&apos;est le <span className="text-pearl font-semibold">SYMPTÔME</span>{" "}
            d&apos;un système de pilotage absent. Si tu ne changes pas le SYSTÈME
            maintenant, dans 12 mois tu seras au même endroit. Plus fatigué.
            Plus seul. Plus loin de la raison pour laquelle tu as commencé.
          </p>
        </div>
      </section>

      {/* ============================== THE BOOTCAMP — KILLER LINE ============================== */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <BluePanel
            className="p-10 sm:p-14 text-center"
            testid="bootcamp-pitch"
          >
            <Zap
              size={48}
              className="mx-auto mb-6"
              style={{
                color: ELECTRIC,
                filter: "drop-shadow(0 0 18px rgba(0,191,255,0.55))",
              }}
            />
            <h2
              className="font-mono font-bold uppercase text-3xl sm:text-5xl tracking-tight mb-8"
              style={{ color: ELECTRIC }}
            >
              Le Bootcamp : ton interrupteur
            </h2>
            <p className="text-lg text-pearl/85 leading-relaxed">
              Zéro blabla. Juste{" "}
              <span style={{ color: ELECTRIC }} className="font-semibold">
                l&apos;essentiel stratégique
              </span>{" "}
              pour comprendre, et le{" "}
              <span style={{ color: ELECTRIC }} className="font-semibold">
                non-négociable
              </span>{" "}
              pour agir. En 30 jours, on installe les 11 livrables ShinRai
              (Neuro-Lean-IA) et tu reprends les commandes de ton temps et de
              ton cash.
            </p>
          </BluePanel>
        </div>
      </section>

      {/* ============================== 11 DELIVERABLES ============================== */}
      <section
        className="relative py-20 sm:py-28 border-y"
        style={{ borderColor: PANEL_BORDER, backgroundColor: "#0c1219" }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            overline="Pas de PowerPoint, pas de blabla. Des outils concrets."
            testid="bootcamp-deliverables-title"
          >
            Les 11 livrables actionnables
          </SectionTitle>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px"
            style={{ backgroundColor: PANEL_BORDER }}
            data-testid="bootcamp-deliverables-grid"
          >
            {DELIVERABLES.map((d, i) => (
              <div
                key={d}
                className="p-6 flex items-start gap-5 transition-colors hover:bg-[#162033]"
                style={{ backgroundColor: PANEL }}
              >
                <div
                  className="flex-shrink-0 font-mono font-bold text-3xl tabular-nums leading-none"
                  style={{ color: ELECTRIC }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="text-pearl/90 text-sm leading-snug pt-1">
                  {d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== DIDIER BAUDE ============================== */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle testid="bootcamp-pilot-title">
            Votre pilote : Didier Baude
          </SectionTitle>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <BluePanel className="overflow-hidden">
                <img
                  src="/brand/didier-baude.jpg"
                  alt="Didier Baude — Coach et Consultant ShinRai"
                  className="w-full h-auto block"
                  data-testid="bootcamp-pilot-photo"
                />
              </BluePanel>
            </div>
            <div className="lg:col-span-7">
              <div
                className="font-mono text-xs tracking-[0.22em] uppercase mb-4"
                style={{ color: ELECTRIC }}
              >
                // Lean · Neurosciences · IA
              </div>
              <blockquote className="text-pearl/85 text-base sm:text-lg leading-relaxed space-y-5 italic">
                <p>
                  « Entrepreneur et ancien Directeur de Projet International,
                  j&apos;ai passé 20 ans à transformer le chaos en flux de
                  valeur. Mon approche unique fusionne la rigueur du Lean
                  Management, la puissance de l&apos;IA (membre de l&apos;écosystème
                  BRAIN) et la finesse des neurosciences (DU en
                  Neuro-éducation).
                </p>
                <p>
                  Coach et Consultant, j&apos;ai conçu ce Bootcamp pour offrir
                  aux dirigeants de TPE/PME les leviers systémiques que
                  j&apos;ai mis des décennies à maîtriser.{" "}
                  <span className="not-italic text-pearl font-semibold">
                    On ne discute pas. On installe. On pilote.
                  </span>{" "}
                  »
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== COMMANDO PROGRAM ============================== */}
      <section
        className="relative py-24 sm:py-32 border-y"
        style={{ borderColor: PANEL_BORDER, backgroundColor: "#0c1219" }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle testid="bootcamp-commando-title">
            « L&apos;accompagnement commando »
          </SectionTitle>
          <p className="text-pearl/75 max-w-3xl mb-12 leading-relaxed">
            On ne te jette pas 120 slides au visage. Le Bootcamp est un parcours
            didactique et progressif. Chaque semaine, une thématique vitale est
            débloquée. Tu regardes, tu comprends, et surtout, tu appliques sur
            ton terrain via des exercices commando. Tu nous envoies ton travail,
            on le corrige, et on valide ensemble lors des calls «{" "}
            <span className="text-pearl">Cerveau Collectif</span> ».
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BluePanel className="p-8">
              <Zap size={28} className="mb-5" style={{ color: ELECTRIC }} />
              <h3 className="font-mono font-bold uppercase text-lg mb-3 text-pearl">
                L&apos;armement stratégique
              </h3>
              <p className="text-pearl/70 text-sm leading-relaxed">
                Accès immédiat à la formation vidéo (Neuro-Lean-IA). Parcours
                progressif, pas un dump de contenu.
              </p>
            </BluePanel>

            <BluePanel className="p-8">
              <Users size={28} className="mb-5" style={{ color: ELECTRIC }} />
              <h3 className="font-mono font-bold uppercase text-lg mb-3 text-pearl">
                Escouade & calls collectifs
              </h3>
              <p className="text-pearl/70 text-sm leading-relaxed mb-4">
                Petit groupe.{" "}
                <span className="text-pearl">Mardi = Installation.</span>{" "}
                <span className="text-pearl">Vendredi = Ajustement.</span> On
                ne te lâche pas.
              </p>
              <ul className="text-pearl/65 text-xs space-y-1.5 font-mono">
                <li>· Mardi (Installation) — <span className="text-pearl">17h00 → 18h30</span></li>
                <li>· Vendredi (Ajustement) — <span className="text-pearl">15h00 → 16h00</span></li>
                <li className="text-pearl/45">Heure de Paris · Créneaux sanctuarisés.</li>
              </ul>
            </BluePanel>

            <BluePanel className="p-8">
              <ShieldCheck size={28} className="mb-5" style={{ color: ELECTRIC }} />
              <h3 className="font-mono font-bold uppercase text-lg mb-3 text-pearl">
                Le non-négociable
              </h3>
              <p className="text-pearl/70 text-sm leading-relaxed">
                À la fin des 30 jours, tes{" "}
                <span className="text-pearl">11 outils de pilotage</span> sont
                installés et fonctionnels. Point.
              </p>
            </BluePanel>
          </div>
        </div>
      </section>

      {/* ============================== ORG & SOUVERAINETÉ ============================== */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <SectionTitle testid="bootcamp-org-title">
            Organisation & souveraineté
          </SectionTitle>

          <BluePanel className="p-8 sm:p-10">
            <p className="text-pearl/80 leading-relaxed mb-6">
              « On ne fait pas les choses n&apos;importe comment. Parce que ton
              agenda est déjà saturé et que les imprévus font partie du métier,
              nous te laissons le temps de t&apos;organiser. L&apos;installation
              d&apos;un système de pilotage demande de la clarté, pas de la
              précipitation. »
            </p>
            <div
              className="flex items-center gap-4 p-5"
              style={{
                backgroundColor: "rgba(0,191,255,0.07)",
                border: `1px solid ${ELECTRIC}40`,
              }}
            >
              <Calendar size={28} style={{ color: ELECTRIC }} />
              <div>
                <div className="font-mono text-xs tracking-[0.22em] uppercase text-pearl/55 mb-1">
                  Démarrage officiel de l&apos;Escouade
                </div>
                <div className="font-mono font-bold text-xl text-pearl">
                  Lundi 22 Juin
                </div>
                <div className="text-pearl/55 text-xs mt-1">
                  Sanctuarise tes créneaux Mardi & Vendredi dès maintenant.
                </div>
              </div>
            </div>
          </BluePanel>
        </div>
      </section>

      {/* ============================== PRICING ============================== */}
      <section
        className="relative py-24 sm:py-32 border-y"
        style={{
          borderColor: PANEL_BORDER,
          backgroundColor: "#0c1219",
        }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-10">
            <div
              className="inline-block font-mono text-xs tracking-[0.28em] uppercase px-3 py-1 mb-6"
              style={{
                color: ELECTRIC,
                border: `1px solid ${ELECTRIC}80`,
              }}
            >
              Offre Pionnier
            </div>
            <h2
              className="font-mono font-bold uppercase text-3xl sm:text-4xl tracking-tight mb-3"
              data-testid="bootcamp-pricing-title"
            >
              10 places seulement
            </h2>
            <p className="text-pearl/60 font-mono text-sm tracking-wide">
              Sélection par appel
            </p>
          </div>

          <BluePanel className="p-8 sm:p-12 text-center">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="font-mono text-2xl text-pearl/40 line-through">
                2 497 €
              </span>
              <span
                className="font-mono font-bold text-6xl sm:text-7xl tabular-nums"
                style={{
                  color: ELECTRIC,
                  textShadow: "0 0 24px rgba(0,191,255,0.4)",
                }}
                data-testid="bootcamp-price"
              >
                497 €
              </span>
            </div>
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-pearl/55 mb-10">
              Économise 2 000 € aujourd&apos;hui
            </div>

            <label className="flex items-start gap-3 text-left text-xs text-pearl/65 leading-relaxed cursor-pointer mb-8">
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="mt-1 flex-shrink-0 accent-[#00BFFF]"
                data-testid="bootcamp-accept-terms"
              />
              <span>
                Je demande l&apos;accès immédiat au contenu numérique et je
                renonce expressément à mon droit de rétractation de 14 jours,
                conformément à l&apos;article L221-28 du Code de la
                consommation.
              </span>
            </label>

            <div className="flex flex-col items-center gap-3">
              {accept ? (
                <PrimaryCTA href={TIDYCAL_URL} testid="bootcamp-pricing-cta">
                  Candidater
                </PrimaryCTA>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-4 px-8 py-5 font-mono font-bold text-sm tracking-[0.18em] uppercase opacity-40 cursor-not-allowed"
                  style={{ backgroundColor: PANEL_BORDER, color: "#ffffff" }}
                  data-testid="bootcamp-pricing-cta-disabled"
                >
                  Acceptez les conditions
                </button>
              )}
              <span
                className="font-mono text-xs tracking-[0.22em] uppercase text-pearl/55"
                data-testid="bootcamp-pricing-caption"
              >
                Appel de 15 minutes
              </span>
            </div>
          </BluePanel>
        </div>
      </section>

      {/* ============================== QUESTION ============================== */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
          <SectionTitle testid="bootcamp-question-title">
            Une question sur le programme ?
          </SectionTitle>
          <p className="text-pearl/75 leading-relaxed mb-10">
            Je réponds personnellement aux futurs pilotes pour m&apos;assurer
            que ce Bootcamp est le bon levier pour votre organisation.
            Échangeons en direct sur WhatsApp.
          </p>

          <a
            href={whatsappUrl(
              "Bonjour Didier, j'ai une question sur le Bootcamp B2B Pilote en 30 jours."
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bootcamp-whatsapp-cta"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white font-mono font-bold text-sm tracking-[0.18em] uppercase hover:bg-[#1ebe57] transition-colors"
          >
            <MessageCircle size={18} strokeWidth={2.5} />
            Discuter avec Didier
          </a>
        </div>
      </section>
    </main>
  );
}
